import {
  Grid,
  TableFilterRow,
  TableHeaderRow,
  VirtualTable,
} from '@devexpress/dx-react-grid-material-ui';
import {
  DateProvider,
  getDatePredicate,
} from '@ozgene/gp5.components.date-provider';
import {useWindowResize} from '@ozgene/gp5.hooks.use-window-resize';
import PropTypes from 'prop-types';
import {useEffect, useState, useCallback} from 'react';
import {Button, Tooltip} from '@material-ui/core';
import {
  Edit as EditIcon,
  Visibility as VisibilityIcon,
} from '@material-ui/icons';
import {
  FilteringState,
  IntegratedFiltering,
  IntegratedSorting,
  SortingState,
} from '@devexpress/dx-react-grid';
import {FilterIcon, filterMessages} from '@ozgene/gp5.utils.filter-functions';
import {ColumnDefinedFilterCell} from 'Components/ColumnDefinedFilterCell';
import {CustomRowComponent} from 'Components/CustomRowComponent';
import CustomTableLoading from 'Utility/CustomTableLoading';
import EntityFilterCell from 'Components/EntityFilterCell';
import SelectTeam from 'Teams/components/SelectTeam';
import {ColumnDefinedCell} from 'Components/ColumnDefinedCell';

export default function ScheduleList(props) {
  const {
    schedules,
    onEditSchedule,
    onViewSchedule,
    scheduleTitleFilter,
    scheduleTeamFilter,
    onChangeScheduleTitleFilter,
    onChangeScheduleTeamFilter,
  } = props;
  const {height} = useWindowResize();
  const [isLoading, setIsLoading] = useState(true);
  const scheduleColumnExtensions = [
    {columnName: 'title', wordWrapEnabled: true},
    {
      columnName: 'modifiedDt',
      width: 200,
      wordWrapEnabled: true,
    },
    {
      columnName: 'actions',
      width: 170,
      sortingEnabled: false,
      filteringEnabled: false,
      wordWrapEnabled: true,
    },
  ];
  const getActionColumn = (row) => (
    <>
      <Tooltip title="Work In Progress">
        <span>
          <Button
            startIcon={<EditIcon />}
            color="secondary"
            onClick={() => onEditSchedule(row)}
            disabled
          >
            Edit Schedule
          </Button>
        </span>
      </Tooltip>
      <Tooltip title="View Schedule">
        <Button
          startIcon={<VisibilityIcon />}
          color="secondary"
          onClick={() => onViewSchedule(row)}
        >
          View Schedule
        </Button>
      </Tooltip>
    </>
  );
  const scheduleColumns = [
    {
      name: 'title',
      title: 'Title',
      getCellValue: (row) => row.title,
    },
    {
      name: 'team',
      title: 'Team',
      getCellValue: (row) => row.team.name,
      CustomCellFilterComponent: EntityFilterCell,
      customCellFilterProps: {
        SelectEntity: SelectTeam,
        getValueIdentifier: ({name}) => name,
      },
    },
    {
      name: 'modifiedDt',
      title: 'Modified Date',
      getCellValue: (row) => row.modifiedDt,
    },
    {
      name: 'actions',
      title: 'Actions',
      getCellValue: getActionColumn,
      CustomCellFilterComponent: false,
    },
  ];
  const scheduleFilteringColumnExtensions = [
    {
      columnName: 'modifiedDt',
      predicate: getDatePredicate,
    },
  ];
  const [filters, setFilters] = useState([
    scheduleTitleFilter.length && {
      columnName: 'title',
      operation: 'contains',
      value: scheduleTitleFilter,
    },
    scheduleTeamFilter.length && {
      columnName: 'team',
      value: scheduleTeamFilter,
    },
  ]);
  const handleFiltersChange = useCallback(
    (newFilters) => {
      const titleFilter = newFilters.find(
        ({columnName}) => columnName === 'title',
      );
      const titleFilterValue = titleFilter?.value ?? '';

      if (titleFilterValue !== scheduleTitleFilter) {
        onChangeScheduleTitleFilter(titleFilterValue);
      }

      const teamFilter = newFilters.find(
        ({columnName}) => columnName === 'team',
      );
      const teamFilterValue = teamFilter?.value ?? '';

      if (teamFilterValue !== scheduleTeamFilter) {
        onChangeScheduleTeamFilter(teamFilterValue);
      }
      setFilters(newFilters);
    },
    [
      onChangeScheduleTitleFilter,
      onChangeScheduleTeamFilter,
      scheduleTitleFilter,
      scheduleTeamFilter,
    ],
  );

  useEffect(() => {
    if (schedules.length > 0) {
      setIsLoading(false);
    }
  }, [schedules]);

  return !isLoading ? (
    <Grid rows={schedules} columns={scheduleColumns}>
      <DateProvider for={['modifiedDt']} />
      <FilteringState filters={filters} onFiltersChange={handleFiltersChange} />
      <SortingState />
      <IntegratedSorting columnExtensions={scheduleColumnExtensions} />
      <IntegratedFiltering
        columnExtensions={scheduleFilteringColumnExtensions}
      />
      <VirtualTable
        height={height - 130}
        columnExtensions={scheduleColumnExtensions}
        rowComponent={CustomRowComponent}
        cellComponent={ColumnDefinedCell}
      />
      <TableHeaderRow showSortingControls />
      <TableFilterRow
        showFilterSelector={true}
        cellComponent={ColumnDefinedFilterCell}
        iconComponent={FilterIcon}
        messages={filterMessages}
      />
    </Grid>
  ) : (
    <CustomTableLoading />
  );
}

ScheduleList.propTypes = {
  schedules: PropTypes.arrayOf(
    PropTypes.shape({
      scheduleId: PropTypes.string,
    }),
  ),
  scheduleTitleFilter: PropTypes.string,
  scheduleTeamFilter: PropTypes.string,
  onEditSchedule: PropTypes.func.isRequired,
  onViewSchedule: PropTypes.func.isRequired,
  onChangeScheduleTitleFilter: PropTypes.func.isRequired,
  onChangeScheduleTeamFilter: PropTypes.func.isRequired,
};

ScheduleList.defaultProps = {
  scheduleTitleFilter: '',
  scheduleTeamFilter: '',
};
